import {
  CircularProgress,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import { WorksheetProduct } from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import OrganizationProductTypeIdsOptions from "modules/OrganizationProductTypes/OrganizationProductTypeIdsOptions/OrganizationProductTypeIdsOptions";
import { unitLabel } from "modules/proposals/worksheet/worksheetFormValues";
import { worksheetCatalogGridTemplate } from "modules/proposals/worksheet/worksheetStyles";

type WorksheetProductsListProps = {
  products: WorksheetProduct[];
  loading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  productTypeId: string;
  onProductTypeChange: (value: string) => void;
  onCreate: () => void;
  onEdit: (product: WorksheetProduct) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
};

const columns = ["Name", "Quantity", "Unit", "Selling Rate", "Cost Rows", ""];

export default function WorksheetProductsList({
  products,
  loading,
  search,
  onSearchChange,
  productTypeId,
  onProductTypeChange,
  onCreate,
  onEdit,
  onDuplicate,
  onDelete,
}: WorksheetProductsListProps) {
  return (
    <UiCard variant="panel">
      <MDBox p="12px" display="flex" flexDirection="column" gap="12px">
        <MDBox display="flex" justifyContent="space-between" alignItems="center" gap="8px">
          <UiTypography uiVariant="heading-sm">Worksheet Products</UiTypography>
          <MDBox display="flex" alignItems="center" gap="8px">
            <OrganizationProductTypeIdsOptions sortByName>
              {({ organizationProductTypeIdsOptions, loading: loadingTypes }) => (
                <FormControl size="small" sx={{ minWidth: 180 }} disabled={loadingTypes}>
                  <InputLabel id="worksheet-product-type-filter-label">Product Type</InputLabel>
                  <Select
                    labelId="worksheet-product-type-filter-label"
                    label="Product Type"
                    value={productTypeId}
                    onChange={(event) => onProductTypeChange(event.target.value)}
                  >
                    <MenuItem value="">All types</MenuItem>
                    {organizationProductTypeIdsOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </OrganizationProductTypeIdsOptions>
            <MDBox sx={{ width: 240 }}>
              <UiInput
                value={search}
                placeholder="Search worksheet products"
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </MDBox>
            <UiButton uiVariant="primary-blue" onClick={onCreate}>
              <Icon fontSize="small">add</Icon>
              New Worksheet Product
            </UiButton>
          </MDBox>
        </MDBox>

        {loading ? (
          <MDBox display="flex" justifyContent="center" p="24px">
            <CircularProgress size={28} />
          </MDBox>
        ) : (
          <MDBox display="flex" flexDirection="column">
            <MDBox
              display="grid"
              gridTemplateColumns={worksheetCatalogGridTemplate}
              px="8px"
              py="6px"
              sx={{ backgroundColor: draftColors.surfaceMuted, borderRadius: "8px" }}
            >
              {columns.map((label) => (
                <UiTypography key={label} uiVariant="overline">
                  {label}
                </UiTypography>
              ))}
            </MDBox>
            {products.map((product) => (
              <MDBox
                key={product.id}
                display="grid"
                gridTemplateColumns={worksheetCatalogGridTemplate}
                px="8px"
                py="8px"
                alignItems="center"
                sx={{ borderBottom: `1px solid ${draftColors.border}` }}
              >
                <MDBox>
                  <UiTypography uiVariant="body-sm" sx={{ fontWeight: 600 }}>
                    {product.name}
                  </UiTypography>
                  {product.organizationProductType?.name && (
                    <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
                      {product.organizationProductType.name}
                    </UiTypography>
                  )}
                </MDBox>
                <UiTypography uiVariant="body-sm">{product.sellingQuantity}</UiTypography>
                <UiTypography uiVariant="body-sm">{unitLabel(product.unit)}</UiTypography>
                <UiTypography uiVariant="body-sm">
                  {formatCentsToUSD(product.sellingRate)}
                </UiTypography>
                <UiTypography uiVariant="body-sm">{product.costRows.length}</UiTypography>
                <MDBox display="flex" gap="6px" justifyContent="flex-end">
                  <Tooltip title="Edit">
                    <Icon
                      fontSize="small"
                      sx={{ cursor: "pointer", color: draftColors.textAction }}
                      onClick={() => onEdit(product)}
                    >
                      edit
                    </Icon>
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <Icon
                      fontSize="small"
                      sx={{ cursor: "pointer", color: draftColors.textAction }}
                      onClick={() => onDuplicate(product.id)}
                    >
                      content_copy
                    </Icon>
                  </Tooltip>
                  <Tooltip title="Archive">
                    <Icon
                      fontSize="small"
                      sx={{ cursor: "pointer", color: draftColors.red }}
                      onClick={() => onDelete(product.id)}
                    >
                      archive
                    </Icon>
                  </Tooltip>
                </MDBox>
              </MDBox>
            ))}
            {products.length === 0 && (
              <UiTypography
                uiVariant="body-sm"
                sx={{ color: draftColors.textSecondary, textAlign: "center", py: "24px" }}
              >
                No worksheet products yet
              </UiTypography>
            )}
          </MDBox>
        )}
      </MDBox>
    </UiCard>
  );
}
